import PaymentService, { PaymentStatuses } from "@/services/PaymentService";
import InespayService, { InespayNoPrice, InespayType } from "@/services/InespayService";
import { parseInespayResult, isInespaySuccess } from "@/utils/InespayResponse";
import { buildContractsElements, buildUpgradeSubject, getGatewayAmountError, TElementLine } from "./upgradeSubscriptionRules";

type ProcessPaymentParams =
{
    // Importe final en céntimos (prorrateado menos Elements). El backend lo recalcula y es quien manda.
    amountCents: number;
    reference: string;
    userId: number;
    // Tarifa actual de la suscripción (oldSubscriptionId).
    currentSubscriptionId: number;
    // Tarifa elegida a la que se mejora.
    subscriptionId: number;
    // Id real de la UsersSubscription que se mejora.
    usersSubscriptionId: number;
    elementLines: TElementLine[];
};

// Resultado del proceso: o se redirige (Inespay o URL de éxito) o se muestra un mensaje al usuario.
export type UpgradePaymentResult =
    | { kind: "redirect"; url: string }
    | { kind: "error"; message: string };

const GenericErrorMessage = "Se ha producido un error al procesar su transacción. Por favor, inténtelo de nuevo más tarde.";
const CreatePaymentErrorMessage = "Error al crear el pago. Por favor, inténtelo de nuevo más tarde.";
const ForbiddenSubscriptionMessage = "No se ha podido realizar la mejora porque la suscripción indicada no pertenece a tu usuario. Si crees que es un error, contacta con soporte.";
const AuthenticationErrorMessage = "Error de autenticación. Por favor, inicia sesión nuevamente.";
const InvalidResponseMessage ="Error al procesar la respuesta del servidor. Por favor, inténtelo de nuevo más tarde.";

/**
 * Mensaje visible para un error de red o HTTP. Los 406/400/409 del backend traen un texto pensado
 * para el usuario en el cuerpo; el resto se sustituye por un mensaje genérico.
 */
const getErrorMessage = (error: any, fallback: string): string =>
{
    const status: number | undefined = error?.response?.status;
    const data = error?.response?.data;

    if(status === 403)
    {
        return ForbiddenSubscriptionMessage;
    }

    if(status === 401)
    {
        return AuthenticationErrorMessage;
    }

    if((status === 400 || status === 406 || status === 409) && typeof data === "string" && data.trim() !== "")
    {
        return data;
    }

    return fallback;
};

const postInespayAndResolve = async (inespayData: InespayType): Promise<UpgradePaymentResult> =>
{
    try
    {
        const dataInespay = await InespayService.postInespay(inespayData);
        const inespayResult = parseInespayResult(dataInespay);

        if(!inespayResult)
        {
            console.error("postInespay: respuesta inválida", dataInespay);
            return { kind: "error", message: InvalidResponseMessage };
        }

        if(isInespaySuccess(inespayResult))
        {
            return { kind: "redirect", url: inespayResult.url };
        }

        return { kind: "error", message: inespayResult.description || GenericErrorMessage };
    }
    catch(error: any)
    {
        console.error("postInespay: error en la llamada", error);
        return { kind: "error", message: getErrorMessage(error, GenericErrorMessage) };
    }
};

/**
 * Mejora de suscripción: crea el pago PENDING y lo envía a /inespay (o /inespayNoPrice si el importe
 * es 0). Mismo flujo que RePaymentSubscription, pero sin renovar fechas (upgradeSubscription: true).
 */
export const processUpgradePayment = async ({
    amountCents,
    reference,
    userId,
    currentSubscriptionId,
    subscriptionId,
    usersSubscriptionId,
    elementLines,
}: ProcessPaymentParams): Promise<UpgradePaymentResult> =>
{
    // Validación previa: no se crea el pago si la pasarela no va a aceptar el importe.
    const amountError = getGatewayAmountError(amountCents);
    if(amountError !== null)
    {
        return { kind: "error", message: amountError };
    }

    // El concepto identifica el pago como mejora en POST /payments (y es el concepto que llega al banco).
    const subject = buildUpgradeSubject(userId, subscriptionId);

    try
    {
        await PaymentService.createPayment(
        {
            amount: amountCents / 100,
            reference,
            status: PaymentStatuses.PENDING,
            subject,
            usersSubscriptionId,
            platform: "INESPAY",
        });
    }
    catch(error: any)
    {
        console.error("processUpgradePayment: error en PaymentService.createPayment()", error);
        return { kind: "error", message: getErrorMessage(error, CreatePaymentErrorMessage) };
    }

    const inespayData: InespayType =
    {
        subject,
        reference,
        amount: String(amountCents),
        subscription: true,
        renovation: true,
        upgradeSubscription: true,
        oldSubscriptionId: currentSubscriptionId,
        subscriptionId,
        usersSubscriptionId,
        userId,
    };

    if(elementLines.length > 0)
    {
        inespayData.contractsElements = buildContractsElements(elementLines);
    }

    if(amountCents > 0)
    {
        return await postInespayAndResolve(inespayData);
    }

    // Importe 0: los Elements cubren el total y no se pasa por Inespay salvo que el backend lo pida.
    let dataNoPrice: InespayNoPrice;
    try
    {
        dataNoPrice = await InespayService.postNoPriceInespay(inespayData);
    }
    catch(error: any)
    {
        console.error("postNoPriceInespay: error en la llamada", error);
        return { kind: "error", message: getErrorMessage(error, GenericErrorMessage) };
    }

    if(!dataNoPrice || dataNoPrice.isError === true)
    {
        return { kind: "error", message: dataNoPrice?.statusMessage || GenericErrorMessage };
    }

    if(dataNoPrice.statusMessage === "Inespay")
    {
        return await postInespayAndResolve(inespayData);
    }

    if(!dataNoPrice.statusMessage)
    {
        return { kind: "error", message: InvalidResponseMessage };
    }

    return { kind: "redirect", url: dataNoPrice.statusMessage };
};
